import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '../../aura';
import { FormContextData, useFormContext } from '../../state';
import { checkTopLevelDomain } from '../../helpers/tld';
import { useValidation } from './helpers/use-validation';
import { useFormErrors } from './helpers/use-form-errors';

const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export function Email(): JSX.Element {
  const { t } = useTranslation('input', { keyPrefix: 'email' });
  const {
    data: { email },
    setData,
  } = useFormContext();

  const validate = useCallback((data: FormContextData) => {
    if (!data.email) {
      return 'Email is empty';
    }
    if (!EMAIL_REGEX.test(data.email)) {
      return 'Email is invalid';
    }
    if (!checkTopLevelDomain(data.email)) {
      return 'Email domain is invalid';
    }
    return null;
  }, []);

  const hasValue = useCallback(
    (data: FormContextData) => (data.email ? null : 'Email is empty'),
    [],
  );
  const extraInputProps = useValidation(t, hasValue, validate);
  const { props: formErrorsProps, clear } = useFormErrors('email');

  const onValueChange = useCallback(
    (value: string) => {
      clear();
      setData(current => ({ ...current, email: value }));
    },
    [setData, clear],
  );

  return (
    <Input
      id="email"
      type="email"
      label={t('label')}
      value={email}
      onValueChange={onValueChange}
      autoComplete="email"
      {...extraInputProps}
      {...formErrorsProps}
    />
  );
}
